import { logger } from '@app/routes/apps/utils/logger';
import { selectOrganizationInfo } from '@app/routes/organization/selectors';
import { analyticsClient } from '@hopin-team/dashboard-shared';
import {
  useDashboardReactiveMap,
  useReadDashboardSharedState,
} from '@hopin-team/dashboard-shared-react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { AnalyticsEvents } from './events';

export function useAnalytics<T extends AnalyticsEvents = AnalyticsEvents>(
  commonProperties?: Record<string, any>,
) {
  const reactiveMap = useDashboardReactiveMap();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const orgInfo = useSelector(selectOrganizationInfo);
  const orgName = orgInfo.name;
  const orgPlan = orgInfo.paymentPlan.planName;

  const track = useCallback(
    async (
      eventName: T,
      payload?: Record<string, any>,
      options?: any,
      callback?: (...params: any[]) => any,
    ) => {
      try {
        const analyticsClient = await reactiveMap.getMapContext(
          'analytics_client',
        );

        if (!analyticsClient) return;

        await analyticsClient.track(
          eventName,
          {
            orgAccountId: orgId,
            orgName,
            orgPlan,
            ...(payload || {}),
            ...(commonProperties || {}),
          },
          options,
          callback,
        );
      } catch (error) {
        console.error('Error tracking event', error);
      }
    },
    [reactiveMap, orgId, orgName, orgPlan, commonProperties],
  );

  const trackAndFlush = useCallback(
    async (eventName: T, payload?: Record<string, any>) => {
      try {
        const analyticsClient = await reactiveMap.getMapContext(
          'analytics_client',
        );

        if (!analyticsClient) {
          logger.warning('Analytics client not available for trackAndFlush');
          return;
        }

        // Call trackAndFlush directly - SDK version is pinned, method is guaranteed to exist
        (analyticsClient as any).trackAndFlush({
          payload: {
            event: eventName,
            anonymousId: orgId,
            properties: {
              orgAccountId: orgId,
              orgName,
              orgPlan,
              ...(payload || {}),
              ...(commonProperties || {}),
            },
          },
        });
        logger.info('Event tracked and flushed via sendBeacon', {
          event: eventName,
        });
      } catch (error) {
        logger.error('Error in trackAndFlush:', error);
        throw error;
      }
    },
    [reactiveMap, orgId, orgName, orgPlan, commonProperties],
  );

  return { track, trackAndFlush };
}

export function trackEvent(
  eventName: AnalyticsEvents,
  payload?: any,
  options?: any,
  callback?: (...params: any[]) => any,
) {
  if (!analyticsClient) return;

  try {
    analyticsClient.track(eventName, payload, options, callback);
  } catch (error) {
    logger.error(`Error tracking event "${eventName}":`, error);
  }
}

export function identify(userId: string) {
  if (!analyticsClient) return;

  try {
    analyticsClient.identify(userId);
  } catch (error) {
    logger.error(`analytics: error identifying user:`, error);
  }
}

export function trackPageView(pageData?: Record<string, any>) {
  if (!analyticsClient) return;

  try {
    analyticsClient.page(pageData);
  } catch (error) {
    logger.error(`analytics: error tracking page view:`, error);
  }
}
